import type { Anchor, DayDef, Opinion, Rounding, ZmanId } from "@kedaat/engine";

type Text = { he: string; en: string };

const SUNRISE: Anchor = { kind: "sunrise", horizon: "sea_level" };
const SUNSET: Anchor = { kind: "sunset", horizon: "sea_level" };
const SUNRISE_ELEVATION: Anchor = { kind: "sunrise", horizon: "elevation" };
const SUNSET_ELEVATION: Anchor = { kind: "sunset", horizon: "elevation" };
const TRANSIT: Anchor = { kind: "transit" };

const dawn = (degrees: number): Anchor => ({ kind: "depression", degrees, dir: "morning" });
const dusk = (degrees: number): Anchor => ({ kind: "depression", degrees, dir: "evening" });
const fixed = (from: Anchor, minutes: number): Anchor => ({
  kind: "offset",
  from,
  minutes,
  unit: "fixed",
});
const zmaniyot = (from: Anchor, minutes: number): Anchor => ({
  kind: "offset",
  from,
  minutes,
  unit: "zmaniyot",
});

/** Definitions of "the day" used for temporal hours. */
export const DAYS = {
  gra: { start: SUNRISE, end: SUNSET },
  mga72: { start: fixed(SUNRISE, -72), end: fixed(SUNSET, 72) },
  mga72Zmaniyot: { start: zmaniyot(SUNRISE, -72), end: zmaniyot(SUNSET, 72) },
  mga90: { start: fixed(SUNRISE, -90), end: fixed(SUNSET, 90) },
  mga16_1: { start: dawn(16.1), end: dusk(16.1) },
  mga19_8: { start: dawn(19.8), end: dusk(19.8) },
  baalHatanya: { start: dawn(1.583), end: dusk(1.583) },
} satisfies Record<string, DayDef>;

type DayKey = keyof typeof DAYS;

const DAY_ID: Record<DayKey, string> = {
  gra: "gra",
  mga72: "mga_72",
  mga72Zmaniyot: "mga_72_zmaniyot",
  mga90: "mga_90",
  mga16_1: "mga_16_1",
  mga19_8: "mga_19_8",
  baalHatanya: "baal_hatanya",
};

const DAY_LABEL: Record<DayKey, Text> = {
  gra: { he: 'גר"א', en: "GRA" },
  mga72: { he: 'מג"א 72 דקות', en: "Magen Avraham 72 minutes" },
  mga72Zmaniyot: { he: 'מג"א 72 דקות זמניות', en: "Magen Avraham 72 temporal minutes" },
  mga90: { he: 'מג"א 90 דקות', en: "Magen Avraham 90 minutes" },
  mga16_1: { he: 'מג"א 16.1°', en: "Magen Avraham 16.1°" },
  mga19_8: { he: 'מג"א 19.8°', en: "Magen Avraham 19.8°" },
  baalHatanya: { he: 'אדמו"ר הזקן (בעל התניא)', en: "Baal HaTanya" },
};

const DAY_TEXT: Record<DayKey, Text> = {
  gra: {
    he: "מהנץ החמה עד השקיעה (מישוריים)",
    en: "sea-level sunrise to sea-level sunset",
  },
  mga72: {
    he: "מעלות השחר (72 דקות לפני הנץ) עד צאת הכוכבים (72 דקות אחרי השקיעה)",
    en: "dawn (72 minutes before sunrise) to nightfall (72 minutes after sunset)",
  },
  mga72Zmaniyot: {
    he: "מעלות השחר עד צאת הכוכבים, 72 דקות זמניות (1.2 שעות זמניות) לפני הנץ ואחרי השקיעה",
    en: "dawn to nightfall, 72 temporal minutes (1.2 temporal hours) before sunrise and after sunset",
  },
  mga90: {
    he: "מעלות השחר (90 דקות לפני הנץ) עד צאת הכוכבים (90 דקות אחרי השקיעה)",
    en: "dawn (90 minutes before sunrise) to nightfall (90 minutes after sunset)",
  },
  mga16_1: {
    he: "מעלות השחר עד צאת הכוכבים, כשהשמש 16.1° מתחת לאופק",
    en: "dawn to nightfall, when the sun is 16.1° below the horizon",
  },
  mga19_8: {
    he: "מעלות השחר עד צאת הכוכבים, כשהשמש 19.8° מתחת לאופק",
    en: "dawn to nightfall, when the sun is 19.8° below the horizon",
  },
  baalHatanya: {
    he: "מהנץ האמיתי עד השקיעה האמיתית, כשמרכז השמש 1.583° מתחת לאופק",
    en: "true sunrise to true sunset, when the sun's center is 1.583° below the horizon",
  },
};

const zmanOf = (id: string) => id.split(".")[0] as ZmanId;

function opinion(
  id: string,
  rounding: Rounding,
  label: Text,
  calc: Anchor,
  explanation: Text,
): Opinion {
  return { id, zman: zmanOf(id), label, calc, rounding, explanation };
}

function byTemporalHours(
  zman: ZmanId,
  hours: number,
  rounding: Rounding,
  dayKeys: DayKey[],
): Opinion[] {
  return dayKeys.map((key) =>
    opinion(
      `${zman}.${DAY_ID[key]}`,
      rounding,
      DAY_LABEL[key],
      { kind: "hours", day: DAYS[key], hours },
      {
        he: `${hours} שעות זמניות מתחילת היום. היום נמנה ${DAY_TEXT[key].he}, ושעה זמנית היא 1/12 ממנו.`,
        en: `${hours} temporal hours into the day. The day runs from ${DAY_TEXT[key].en}; a temporal hour is 1/12 of it.`,
      },
    ),
  );
}

const degreesText = (degrees: number, when: "before" | "after"): Text => ({
  he: `כשמרכז השמש ${degrees}° מתחת לאופק ${when === "before" ? "לפני הזריחה" : "אחרי השקיעה"} (חישוב גאומטרי, ללא תלות בגובה).`,
  en: `When the sun's center is ${degrees}° below the horizon ${when === "before" ? "before sunrise" : "after sunset"} (geometric, independent of elevation).`,
});

const minutesText = (
  minutes: number,
  unit: "fixed" | "zmaniyot",
  when: "before" | "after",
): Text => ({
  he:
    `${minutes} דקות ${unit === "zmaniyot" ? "זמניות " : ""}${when === "before" ? "לפני הנץ" : "אחרי השקיעה"} המישוריים.` +
    (unit === "zmaniyot"
      ? " דקה זמנית היא 1/60 משעה זמנית (1/12 מהזמן שבין הנץ לשקיעה), ולכן ארוכה בקיץ וקצרה בחורף."
      : ""),
  en:
    `${minutes} ${unit === "zmaniyot" ? "temporal " : ""}minutes ${when === "before" ? "before sea-level sunrise" : "after sea-level sunset"}.` +
    (unit === "zmaniyot"
      ? " A temporal minute is 1/60 of a temporal hour (1/12 of sunrise to sunset), so it is longer in summer."
      : ""),
});

export const OPINIONS: readonly Opinion[] = [
  // עלות השחר
  opinion("alos.deg_16_1", "ceil", { he: "16.1°", en: "16.1°" }, dawn(16.1), {
    he: `${degreesText(16.1, "before").he} שווה ל-72 דקות לפני הנץ בירושלים בתקופת ניסן ותשרי.`,
    en: `${degreesText(16.1, "before").en} Equivalent to 72 minutes before sunrise in Jerusalem at the equinox.`,
  }),
  opinion("alos.deg_19_8", "ceil", { he: "19.8°", en: "19.8°" }, dawn(19.8), {
    he: `${degreesText(19.8, "before").he} שווה ל-90 דקות לפני הנץ בירושלים בתקופת ניסן ותשרי.`,
    en: `${degreesText(19.8, "before").en} Equivalent to 90 minutes before sunrise in Jerusalem at the equinox.`,
  }),
  opinion(
    "alos.deg_16_9",
    "ceil",
    { he: 'אדמו"ר הזקן (16.9°)', en: "Baal HaTanya (16.9°)" },
    dawn(16.9),
    degreesText(16.9, "before"),
  ),
  opinion(
    "alos.deg_15_99",
    "ceil",
    { he: 'לוח חב"ד (16°)', en: "Chabad luach (16°)" },
    dawn(15.99),
    {
      he: `${degreesText(15.99, "before").he} זהו הערך שבו נוקט הלוח של חב"ד בישראל, ובעיגול הוא 16°.`,
      en: `${degreesText(15.99, "before").en} This is the value used by the Chabad Israel luach, 16° when rounded.`,
    },
  ),
  opinion(
    "alos.min_72",
    "ceil",
    { he: "72 דקות", en: "72 minutes" },
    fixed(SUNRISE, -72),
    minutesText(72, "fixed", "before"),
  ),
  opinion(
    "alos.zmaniyot_72",
    "ceil",
    { he: "72 דקות זמניות", en: "72 temporal minutes" },
    zmaniyot(SUNRISE, -72),
    minutesText(72, "zmaniyot", "before"),
  ),
  opinion(
    "alos.min_90",
    "ceil",
    { he: "90 דקות", en: "90 minutes" },
    fixed(SUNRISE, -90),
    minutesText(90, "fixed", "before"),
  ),
  opinion(
    "alos.zmaniyot_90",
    "ceil",
    { he: "90 דקות זמניות", en: "90 temporal minutes" },
    zmaniyot(SUNRISE, -90),
    minutesText(90, "zmaniyot", "before"),
  ),

  // משיכיר
  opinion(
    "misheyakir.deg_10_2",
    "ceil",
    { he: "10.2°", en: "10.2°" },
    dawn(10.2),
    degreesText(10.2, "before"),
  ),
  opinion(
    "misheyakir.deg_11",
    "ceil",
    { he: "11°", en: "11°" },
    dawn(11),
    degreesText(11, "before"),
  ),
  opinion(
    "misheyakir.deg_11_5",
    "ceil",
    { he: "11.5°", en: "11.5°" },
    dawn(11.5),
    degreesText(11.5, "before"),
  ),

  opinion(
    "misheyakir.zmaniyot_66",
    "ceil",
    { he: "66 דקות זמניות (1.1 שעות)", en: "66 temporal minutes (1.1 hours)" },
    zmaniyot(SUNRISE, -66),
    {
      he: `${minutesText(66, "zmaniyot", "before").he} כך בלוח אור החיים, והוא 6 דקות זמניות אחרי עלות השחר שלו.`,
      en: `${minutesText(66, "zmaniyot", "before").en} This is the Or HaChaim luach's definition, six temporal minutes after its dawn.`,
    },
  ),

  // הנץ החמה
  opinion("sunrise.sea_level", "ceil", { he: "מישורי", en: "Sea level" }, SUNRISE, {
    he: "הזריחה בגובה פני הים: השפה העליונה של השמש נראית באופק (כולל 34′ שבירת אור ו-16′ חצי קוטר השמש). זו ברירת המחדל.",
    en: "Sea-level sunrise: the sun's upper edge on the horizon (including 34′ refraction and 16′ solar radius). This is the default.",
  }),
  opinion(
    "sunrise.elevation",
    "ceil",
    { he: "לפי גובה", en: "Elevation-adjusted" },
    SUNRISE_ELEVATION,
    {
      he: "הזריחה כפי שנראית מגובה המקום מעל האופק שמסביבו. במקומות גבוהים מוקדמת מהזריחה המישורית. במקומות נמוכים מפני הים מחושב כמישורי.",
      en: "Sunrise as seen from the location's height above its surrounding horizon. Earlier than sea-level sunrise in high places; below sea level it equals sea level.",
    },
  ),

  // סוף זמן קריאת שמע ותפילה
  ...byTemporalHours("sof_zman_shma", 3, "floor", [
    "gra",
    "mga72",
    "mga72Zmaniyot",
    "mga90",
    "mga16_1",
    "mga19_8",
    "baalHatanya",
  ]),
  opinion(
    "sof_zman_shma.chabad_luach",
    "floor",
    { he: 'לוח חב"ד (גר"א פחות 2 דקות)', en: "Chabad luach (GRA less 2 minutes)" },
    {
      kind: "offset",
      from: { kind: "hours", day: DAYS.gra, hours: 3 },
      minutes: -2,
      unit: "fixed",
    },
    {
      he: '3 שעות זמניות מהנץ (כגר"א), פחות שתי דקות. כך נוהג הלוח של חב"ד בישראל, להחמיר.',
      en: "Three temporal hours from sunrise (as GRA), less two minutes, as a stringency in the Chabad Israel luach.",
    },
  ),
  ...byTemporalHours("sof_zman_tfila", 4, "floor", [
    "gra",
    "mga72",
    "mga72Zmaniyot",
    "mga90",
    "mga16_1",
    "mga19_8",
    "baalHatanya",
  ]),
  ...byTemporalHours("sof_zman_achilat_chametz", 4, "floor", [
    "gra",
    "mga72",
    "mga72Zmaniyot",
    "mga16_1",
    "baalHatanya",
  ]),
  ...byTemporalHours("sof_zman_biur_chametz", 5, "floor", [
    "gra",
    "mga72",
    "mga72Zmaniyot",
    "mga16_1",
    "baalHatanya",
  ]),

  // חצות היום
  opinion("chatzot.astronomical", "floor", { he: "חצות אסטרונומית", en: "Solar noon" }, TRANSIT, {
    he: "הרגע שבו השמש במרום הרקיע (עוברת את קו האורך של המקום).",
    en: "The moment the sun crosses the local meridian (its highest point).",
  }),
  opinion(
    "chatzot.half_day",
    "floor",
    { he: "אמצע היום", en: "Half of the day" },
    { kind: "midpoint", a: SUNRISE, b: SUNSET },
    {
      he: "אמצע הזמן שבין הנץ לשקיעה המישוריים. שונה מחצות האסטרונומית בשניות בודדות.",
      en: "Halfway between sea-level sunrise and sunset. Differs from solar noon by a few seconds.",
    },
  ),

  // מנחה גדולה
  ...byTemporalHours("mincha_gedola", 6.5, "ceil", ["gra", "mga72", "mga16_1", "baalHatanya"]),
  opinion(
    "mincha_gedola.min_30",
    "ceil",
    { he: "30 דקות אחרי חצות", en: "30 minutes after chatzot" },
    fixed(TRANSIT, 30),
    {
      he: "30 דקות קבועות אחרי חצות היום האסטרונומית.",
      en: "30 fixed minutes after solar noon.",
    },
  ),
  opinion(
    "mincha_gedola.gra_greater_than_30",
    "ceil",
    { he: 'גר"א, ולא פחות מ-30 דקות', en: "GRA, at least 30 minutes" },
    { kind: "latest", of: [{ kind: "hours", day: DAYS.gra, hours: 6.5 }, fixed(TRANSIT, 30)] },
    {
      he: 'המאוחר מבין חצי שעה זמנית אחרי חצות (גר"א) ו-30 דקות קבועות אחרי חצות. בחורף, כשהשעה הזמנית קצרה, מחמירים ב-30 דקות.',
      en: "The later of half a temporal hour after chatzot (GRA) and 30 fixed minutes after solar noon. In winter, when temporal hours are short, the 30 minutes apply.",
    },
  ),

  // מנחה קטנה ופלג המנחה
  ...byTemporalHours("mincha_ketana", 9.5, "ceil", ["gra", "mga72", "mga16_1", "baalHatanya"]),
  ...byTemporalHours("plag_hamincha", 10.75, "ceil", [
    "gra",
    "mga72",
    "mga72Zmaniyot",
    "mga90",
    "mga16_1",
    "mga19_8",
    "baalHatanya",
  ]),

  // שקיעה
  opinion("sunset.sea_level", "floor", { he: "מישורית", en: "Sea level" }, SUNSET, {
    he: "השקיעה בגובה פני הים: השפה העליונה של השמש נעלמת באופק. זו ברירת המחדל, והיא המחמירה במקומות גבוהים.",
    en: "Sea-level sunset: the sun's upper edge disappears on the horizon. This is the default, and the stringent choice in high places.",
  }),
  opinion(
    "sunset.elevation",
    "floor",
    { he: "לפי גובה", en: "Elevation-adjusted" },
    SUNSET_ELEVATION,
    {
      he: "השקיעה כפי שנראית מגובה המקום מעל האופק שמסביבו. במקומות גבוהים מאוחרת מהשקיעה המישורית (בירושלים כ-4 דקות). יש לסמוך עליה רק במקום שגבוה בבירור מסביבתו ויש לו אופק מערבי פתוח, ובהוראת רב.",
      en: "Sunset as seen from the location's height above its surrounding horizon. Later than sea-level sunset in high places (about 4 minutes in Jerusalem). Rely on it only where the place is clearly higher than its surroundings with an open western horizon, and with rabbinic guidance.",
    },
  ),

  // צאת הכוכבים
  opinion(
    "tzeit.deg_8_5",
    "ceil",
    { he: "8.5° (ג' כוכבים קטנים)", en: "8.5° (three small stars)" },
    dusk(8.5),
    degreesText(8.5, "after"),
  ),
  opinion(
    "tzeit.deg_7_083",
    "ceil",
    { he: "7.083° (ג' כוכבים בינוניים)", en: "7.083° (three medium stars)" },
    dusk(7.083),
    degreesText(7.083, "after"),
  ),
  opinion(
    "tzeit.deg_6_45",
    "ceil",
    { he: "6.45° (ר' טוקצ'ינסקי)", en: "6.45° (R. Tukachinsky)" },
    dusk(6.45),
    degreesText(6.45, "after"),
  ),
  opinion(
    "tzeit.deg_6",
    "ceil",
    { he: 'אדמו"ר הזקן (6°)', en: "Baal HaTanya (6°)" },
    dusk(6),
    degreesText(6, "after"),
  ),
  opinion(
    "tzeit.deg_5_88",
    "ceil",
    { he: 'לוח חב"ד (5.88°)', en: "Chabad luach (5.88°)" },
    dusk(5.88),
    degreesText(5.88, "after"),
  ),
  opinion(
    "tzeit.deg_9_28",
    "ceil",
    { he: "9.28° (חזון איש)", en: "9.28° (Chazon Ish)" },
    dusk(9.28),
    {
      he: `${degreesText(9.28, "after").he} כארבעים דקות אחרי השקיעה בימי השוויון, והוא הזמן המיוחס לחזון איש.`,
      en: `${degreesText(9.28, "after").en} About forty minutes after sunset at the equinox; the time attributed to the Chazon Ish.`,
    },
  ),
  opinion(
    "tzeit.zmaniyot_13_5",
    "ceil",
    { he: "13.5 דקות זמניות", en: "13.5 temporal minutes" },
    zmaniyot(SUNSET, 13.5),
    minutesText(13.5, "zmaniyot", "after"),
  ),
  opinion(
    "tzeit.min_18",
    "ceil",
    { he: "18 דקות", en: "18 minutes" },
    fixed(SUNSET, 18),
    minutesText(18, "fixed", "after"),
  ),
  opinion(
    "tzeit.min_20",
    "ceil",
    { he: "20 דקות", en: "20 minutes" },
    fixed(SUNSET, 20),
    minutesText(20, "fixed", "after"),
  ),

  // צאת הכוכבים לרבנו תם
  opinion(
    "tzeit_rt.min_72",
    "ceil",
    { he: "72 דקות", en: "72 minutes" },
    fixed(SUNSET, 72),
    minutesText(72, "fixed", "after"),
  ),
  opinion(
    "tzeit_rt.zmaniyot_72",
    "ceil",
    { he: "72 דקות זמניות", en: "72 temporal minutes" },
    zmaniyot(SUNSET, 72),
    minutesText(72, "zmaniyot", "after"),
  ),
  opinion(
    "tzeit_rt.min_90",
    "ceil",
    { he: "90 דקות", en: "90 minutes" },
    fixed(SUNSET, 90),
    minutesText(90, "fixed", "after"),
  ),
  opinion(
    "tzeit_rt.deg_16_1",
    "ceil",
    { he: "16.1°", en: "16.1°" },
    dusk(16.1),
    degreesText(16.1, "after"),
  ),

  // חצות הלילה
  opinion(
    "chatzot_night.half_night",
    "floor",
    { he: "אמצע הלילה", en: "Half of the night" },
    { kind: "midpoint", a: SUNSET, b: { kind: "nextDay", anchor: SUNRISE } },
    {
      he: "אמצע הזמן שבין השקיעה לנץ החמה של מחר (מישוריים).",
      en: "Halfway between sea-level sunset and the next sea-level sunrise.",
    },
  ),
];
